import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Copy, Check, Envelope, LinkSimple, PaperPlaneTilt, User, CheckCircle, WarningCircle, ShieldCheck, Lock, Clock, Calendar } from "@phosphor-icons/react";
import PatientAutocomplete from "@/components/PatientAutocomplete";
import { useAccessGate } from "@/hooks/useAccessGate";

// ---------- Step 1 : Patient picker ----------
function StepPatient({ patients, patientId, setPatientId, presetPatientId, selected }) {
  return (
    <div>
      <p className="text-[11px] tracking-[0.14em] uppercase text-[#5F758F] font-semibold mb-2 flex items-center gap-1.5">
        <User size={12} weight="fill" className="text-[#3A8FA5]" /> Étape 1 · Patient
      </p>
      {patients.length === 0 ? (
        <div className="rounded-[10px] bg-[#FFE0DC] border border-[#EEC1BB] px-4 py-3 text-[13px] text-[#8B2A1E] flex gap-2">
          <WarningCircle size={16} weight="fill" className="shrink-0 mt-0.5" />
          <span>Aucun patient enregistré. Créez-en un depuis <em>Patients</em> avant d'envoyer.</span>
        </div>
      ) : presetPatientId && selected ? (
        <div className="rounded-[10px] bg-[#E6F1F5] border border-[#C7DEE6] px-4 py-3" data-testid="send-preset-patient">
          <p className="font-semibold text-[#1E3A5F]">{selected.first_name} {selected.last_name}</p>
          <p className="text-[12px] text-[#5F758F] mt-0.5">
            {selected.date_of_birth && <>Né(e) le {new Date(selected.date_of_birth).toLocaleDateString("fr-FR")}</>}
            {selected.email && <> · <span data-testid="send-preset-patient-email">{selected.email}</span></>}
          </p>
        </div>
      ) : (
        <PatientAutocomplete patients={patients} value={patientId} onChange={setPatientId} placeholder="Rechercher par nom, prénom…" />
      )}
    </div>
  );
}

// ---------- Step 2 : Method picker + inline email add ----------
function MethodCard({ active, icon: Icon, label, hint, onClick, testId }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId}
      className={`rounded-[10px] border px-4 py-3 text-left transition-colors ${active ? "border-[#3A8FA5] bg-[#E6F1F5] ring-1 ring-[#3A8FA5]" : "border-[#E1E9F0] bg-white hover:border-[#C7DEE6]"}`}>
      <Icon size={16} weight="duotone" className={active ? "text-[#3A8FA5]" : "text-[#5F758F]"} />
      <p className={`text-[13px] font-semibold mt-1 ${active ? "text-[#1E3A5F]" : "text-[#5F758F]"}`}>{label}</p>
      <p className="text-[11px] text-[#5F758F] mt-0.5">{hint}</p>
    </button>
  );
}

function InlineAddEmail({ selected, busy, onSaved, setBusy }) {
  const [draft, setDraft] = useState("");
  const [err, setErr] = useState("");
  const save = async () => {
    const em = draft.trim().toLowerCase();
    if (!em || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) { setErr("Email invalide"); return; }
    setErr(""); setBusy(true);
    try {
      await api.put(`/patient-records/${selected.patient_id}`, {
        first_name: selected.first_name, last_name: selected.last_name,
        date_of_birth: selected.date_of_birth || null, email: em, sex: selected.sex || null,
      });
      toast.success("Email ajouté à la fiche patient"); setDraft(""); await onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-3 rounded-[10px] bg-[#FFF3D6] border border-[#F0DDA8] px-4 py-3">
      <p className="text-[12px] text-[#7A4C0B] mb-2">Ce patient n'a pas encore d'email. Ajoutez-le pour permettre l'envoi :</p>
      <div className="flex gap-2">
        <Input type="email" value={draft} onChange={(e) => { setDraft(e.target.value); if (err) setErr(""); }}
          placeholder="patient@exemple.com"
          className={`bg-white text-[13px] h-9 ${err ? "border-red-500 focus-visible:ring-red-500" : "border-[#F0DDA8]"}`}
          data-testid="send-email-draft" />
        <Button size="sm" variant="outline" className="rounded-[8px] shrink-0 h-9"
          onClick={save} disabled={busy || !draft.trim()} data-testid="send-email-save">Enregistrer</Button>
      </div>
      {err && <p className="text-[11px] text-red-600 mt-1" data-testid="send-email-error">{err}</p>}
    </div>
  );
}

function StepMethod({ tab, setTab, selected, canEmail, busy, setBusy, reloadPatients }) {
  return (
    <div>
      <p className="text-[11px] tracking-[0.14em] uppercase text-[#5F758F] font-semibold mb-2">Étape 2 · Comment l'envoyer ?</p>
      <div className="grid grid-cols-2 gap-2">
        <MethodCard active={tab === "email"} icon={Envelope} label="Email au patient" hint="Envoi immédiat" onClick={() => setTab("email")} testId="tab-email" />
        <MethodCard active={tab === "link"} icon={LinkSimple} label="Lien à partager" hint="SMS, WhatsApp, imprimé…" onClick={() => setTab("link")} testId="tab-link" />
      </div>
      {tab === "email" && selected && !canEmail && (
        <InlineAddEmail selected={selected} busy={busy} setBusy={setBusy} onSaved={reloadPatients} />
      )}
      {tab === "email" && canEmail && (
        <p className="mt-2 text-[12px] text-[#5F758F]">Sera envoyé à <strong className="text-[#1E3A5F]">{selected.email}</strong>.</p>
      )}
    </div>
  );
}

// ---------- Step 3 : Scheduling ----------
function StepSchedule({ whenMode, setWhenMode, scheduledFor, setScheduledFor, minLocalDatetime }) {
  return (
    <div>
      <p className="text-[11px] tracking-[0.14em] uppercase text-[#5F758F] font-semibold mb-2 flex items-center gap-1.5">
        <Clock size={12} weight="fill" className="text-[#3A8FA5]" /> Étape 3 · Quand l'envoyer ?
      </p>
      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={() => setWhenMode("now")}
          data-testid="when-now"
          className={`rounded-[10px] border px-4 py-3 text-left transition-colors ${whenMode === "now" ? "border-[#3A8FA5] bg-[#E6F1F5] ring-1 ring-[#3A8FA5]" : "border-[#E1E9F0] bg-white hover:border-[#C7DEE6]"}`}
        >
          <PaperPlaneTilt size={16} weight="duotone" className={whenMode === "now" ? "text-[#3A8FA5]" : "text-[#5F758F]"} />
          <p className={`text-[13px] font-semibold mt-1 ${whenMode === "now" ? "text-[#1E3A5F]" : "text-[#5F758F]"}`}>Maintenant</p>
          <p className="text-[11px] text-[#5F758F] mt-0.5">Le patient reçoit immédiatement</p>
        </button>
        <button
          type="button"
          onClick={() => setWhenMode("later")}
          data-testid="when-later"
          className={`rounded-[10px] border px-4 py-3 text-left transition-colors ${whenMode === "later" ? "border-[#3A8FA5] bg-[#E6F1F5] ring-1 ring-[#3A8FA5]" : "border-[#E1E9F0] bg-white hover:border-[#C7DEE6]"}`}
        >
          <Calendar size={16} weight="duotone" className={whenMode === "later" ? "text-[#3A8FA5]" : "text-[#5F758F]"} />
          <p className={`text-[13px] font-semibold mt-1 ${whenMode === "later" ? "text-[#1E3A5F]" : "text-[#5F758F]"}`}>Programmer</p>
          <p className="text-[11px] text-[#5F758F] mt-0.5">Envoi différé à une date</p>
        </button>
      </div>
      {whenMode === "later" && (
        <div className="mt-3">
          <Input
            type="datetime-local"
            value={scheduledFor}
            min={minLocalDatetime}
            onChange={(e) => setScheduledFor(e.target.value)}
            className="bg-white border-[#E1E9F0] text-[14px]"
            data-testid="scheduled-for"
          />
          <p className="text-[11px] text-[#5F758F] mt-1.5">L'email partira automatiquement à la date choisie (fuseau Europe/Paris).</p>
        </div>
      )}
    </div>
  );
}

// ---------- Result panels ----------
function ResultEmailSent({ selected }) {
  return (
    <div className="text-center py-4">
      <div className="mx-auto w-14 h-14 rounded-full bg-[#E6F1F5] border-2 border-[#3A8FA5] flex items-center justify-center mb-4">
        <CheckCircle size={32} weight="fill" className="text-[#3A8FA5]" />
      </div>
      <p className="text-2xl font-bold tracking-tight text-[#1E3A5F] mb-2">Email envoyé</p>
      <p className="text-[14px] text-[#5F758F] leading-relaxed">
        Un lien sécurisé vient d'être envoyé à<br />
        <strong className="text-[#1E3A5F]">{selected?.email}</strong>
      </p>
      <div className="mt-5 rounded-[10px] bg-[#F4F7FB] border border-[#E1E9F0] px-4 py-3 text-[12px] text-[#5F758F] text-left">
        <p className="font-semibold text-[#1E3A5F] mb-1">📬 Et maintenant ?</p>
        Le patient recevra l'email dans quelques secondes. Retrouvez la réponse dans <strong>Envois</strong> ou sur sa fiche patient.
      </div>
    </div>
  );
}

function ResultScheduled({ selected, scheduledFor }) {
  const dt = new Date(scheduledFor);
  const dateStr = dt.toLocaleString("fr-FR", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" });
  return (
    <div className="text-center py-4" data-testid="send-result-scheduled">
      <div className="mx-auto w-14 h-14 rounded-full bg-[#F2E7F5] border-2 border-[#7A4EA8] flex items-center justify-center mb-4">
        <Calendar size={30} weight="duotone" className="text-[#7A4EA8]" />
      </div>
      <p className="text-2xl font-bold tracking-tight text-[#1E3A5F] mb-2">Envoi programmé</p>
      <p className="text-[14px] text-[#5F758F] leading-relaxed">
        L'email sera envoyé automatiquement à<br />
        <strong className="text-[#1E3A5F]">{selected?.email}</strong><br />
        le <strong className="text-[#1E3A5F]">{dateStr}</strong>.
      </p>
      <div className="mt-5 rounded-[10px] bg-[#F4F7FB] border border-[#E1E9F0] px-4 py-3 text-[12px] text-[#5F758F] text-left">
        <p className="font-semibold text-[#1E3A5F] mb-1">Modifier la programmation ?</p>
        Vous pouvez annuler cet envoi à tout moment depuis l'onglet <strong>Envois</strong> ou depuis la fiche du patient (tant qu'il n'a pas encore été envoyé).
      </div>
    </div>
  );
}

function ResultLinkReady({ link }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(link); setCopied(true); toast.success("Lien copié"); setTimeout(() => setCopied(false), 1500); }
    catch { toast.error("Copie impossible"); }
  };
  return (
    <div>
      <div className="mx-auto w-14 h-14 rounded-full bg-[#E6F1F5] border-2 border-[#3A8FA5] flex items-center justify-center mb-4">
        <LinkSimple size={26} weight="duotone" className="text-[#3A8FA5]" />
      </div>
      <p className="text-2xl font-bold tracking-tight text-[#1E3A5F] text-center mb-2">Lien prêt</p>
      <p className="text-[13px] text-[#5F758F] text-center mb-4">Copiez ce lien unique et transmettez-le au patient par le moyen de votre choix (SMS, WhatsApp, papier…).</p>
      <div className="flex gap-2">
        <Input value={link} readOnly className="bg-[#F4F7FB] border-[#E1E9F0] text-[12.5px] font-mono" data-testid="send-result-link" />
        <Button className="rounded-[10px] bg-[#3A8FA5] hover:bg-[#164E5F] text-white shrink-0 px-4" onClick={copy} data-testid="send-copy">
          {copied ? <><Check size={14} className="mr-1.5" /> Copié</> : <><Copy size={14} className="mr-1.5" /> Copier</>}
        </Button>
      </div>
      <div className="mt-4 rounded-[10px] bg-[#F4F7FB] border border-[#E1E9F0] px-4 py-3 text-[12px] text-[#5F758F]">
        <p className="font-semibold text-[#1E3A5F] mb-1">🔒 Sécurité</p>
        Ce lien est <strong>personnel et unique</strong>. Il ne peut être utilisé qu'une fois. La réponse arrivera automatiquement sur la fiche du patient.
      </div>
    </div>
  );
}

// ---------- Main dialog ----------
// Helper: build a value acceptable by <input type="datetime-local"> in the
// user's local timezone, using a small offset (in minutes) from *now*.
function localDatetimeAt(offsetMin) {
  const d = new Date(Date.now() + offsetMin * 60_000);
  d.setSeconds(0, 0);
  const tz = d.getTimezoneOffset();
  return new Date(d.getTime() - tz * 60_000).toISOString().slice(0, 16);
}

export default function SendDialog({ template, presetPatientId, onClose }) {
  const [tab, setTab] = useState("email");
  const [patients, setPatients] = useState([]);
  const [patientId, setPatientId] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [whenMode, setWhenMode] = useState("now"); // "now" | "later"
  const [scheduledFor, setScheduledFor] = useState(""); // datetime-local string
  const { isTrialExpired, gateProps } = useAccessGate();

  const open = !!template;
  const load = async () => { const { data } = await api.get("/patient-records"); setPatients(data); };
  useEffect(() => {
    if (open) {
      load(); setResult(null); setPatientId(presetPatientId || "");
      setTab("email"); setWhenMode("now"); setScheduledFor(localDatetimeAt(60));
    }
  }, [open, presetPatientId]);

  const selected = patients.find((p) => p.patient_id === patientId);
  const canEmail = !!(selected && selected.email);
  const minLocalDatetime = localDatetimeAt(15);
  const wantsScheduled = tab === "email" && whenMode === "later";
  const scheduledIsValid = !wantsScheduled || (scheduledFor && scheduledFor >= minLocalDatetime);

  const submit = async () => {
    if (!patientId) return toast.error("Sélectionnez un patient");
    if (tab === "email" && !canEmail) return toast.error("Ce patient n'a pas d'email — passez en lien partageable");
    if (wantsScheduled && !scheduledIsValid) return toast.error("La date doit être au moins 15 minutes dans le futur");
    setBusy(true);
    try {
      const payload = { patient_id: patientId, method: tab };
      if (wantsScheduled) {
        // Convert local datetime → UTC ISO string
        payload.scheduled_for = new Date(scheduledFor).toISOString();
      }
      const { data } = await api.post(`/templates/${template.template_id}/send`, payload);
      setResult(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  const close = () => { setResult(null); setPatientId(""); setTab("email"); setWhenMode("now"); onClose(); };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); }}>
      <DialogContent className="bg-white max-w-lg p-0 overflow-hidden gap-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-[#E1E9F0]">
          <DialogTitle className="flex items-center gap-2 text-xl font-bold tracking-tight text-[#1E3A5F]">
            <PaperPlaneTilt size={20} weight="duotone" className="text-[#3A8FA5]" />
            {result ? (
              result.scheduled ? "Envoi programmé" :
              tab === "email" ? "Email envoyé" : "Lien prêt à partager"
            ) : "Envoyer un questionnaire"}
          </DialogTitle>
          {!result && (
            <p className="text-[13px] text-[#5F758F] mt-1 pr-6 leading-relaxed">
              <span className="font-semibold text-[#1E3A5F]">{template?.title}</span>
            </p>
          )}
        </DialogHeader>

        <div className="px-6 py-5 space-y-5 max-h-[70vh] overflow-y-auto">
          {isTrialExpired && !result && (
            <div className="rounded-[10px] bg-[#FFF3D6] border border-[#F0DDA8] px-4 py-3 flex items-start gap-2" data-testid="send-trial-expired-banner">
              <Lock size={16} weight="duotone" className="text-[#8B2A1E] shrink-0 mt-0.5" />
              <div className="text-[13px] text-[#7A4C0B] leading-relaxed">
                <p className="font-semibold text-[#8B2A1E] mb-0.5">Période d'essai terminée</p>
                Souscrivez à un abonnement pour envoyer à nouveau des questionnaires à vos patients.
              </div>
            </div>
          )}
          {!result ? (
            <>
              <StepPatient patients={patients} patientId={patientId} setPatientId={setPatientId}
                presetPatientId={presetPatientId} selected={selected} />
              <StepMethod tab={tab} setTab={setTab} selected={selected} canEmail={canEmail}
                busy={busy} setBusy={setBusy} reloadPatients={load} />
              {tab === "email" && canEmail && (
                <StepSchedule
                  whenMode={whenMode} setWhenMode={setWhenMode}
                  scheduledFor={scheduledFor} setScheduledFor={setScheduledFor}
                  minLocalDatetime={minLocalDatetime}
                />
              )}
              <div className="rounded-[10px] bg-[#F4F7FB] border border-[#E1E9F0] px-3 py-2 text-[11px] text-[#5F758F] flex items-start gap-2">
                <ShieldCheck size={13} weight="duotone" className="text-[#3A8FA5] shrink-0 mt-0.5" />
                <span>L'email ne contient ni le nom du patient ni celui du questionnaire — les données de santé restent derrière le lien authentifié (RGPD).</span>
              </div>
            </>
          ) : result.scheduled ? (
            <ResultScheduled selected={selected} scheduledFor={result.scheduled_for} />
          ) : tab === "email" ? (
            <ResultEmailSent selected={selected} />
          ) : (
            <ResultLinkReady link={result.link} />
          )}
        </div>

        <div className="border-t border-[#E1E9F0] px-6 py-3 flex justify-end gap-2 bg-[#F4F7FB]">
          {!result ? (
            <>
              <Button variant="ghost" onClick={close}>Annuler</Button>
              <Button
                className="rounded-[10px] bg-[#3A8FA5] hover:bg-[#164E5F] text-white font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="send-submit"
                {...gateProps({
                  onClick: submit,
                  disabled: busy || !patientId || (tab === "email" && !canEmail) || !scheduledIsValid,
                  title: wantsScheduled ? "Programmer l'envoi" : (tab === "email" ? "Envoyer l'email" : "Générer le lien"),
                })}
              >
                {busy ? "Envoi…" : wantsScheduled ? "Programmer l'envoi" : tab === "email" ? "Envoyer l'email" : "Générer le lien"}
                {!busy && (wantsScheduled ? <Calendar size={14} className="ml-2" /> : <PaperPlaneTilt size={14} className="ml-2" />)}
              </Button>
            </>
          ) : (
            <Button className="rounded-[10px] bg-[#3A8FA5] hover:bg-[#164E5F] text-white font-semibold" onClick={close} data-testid="send-done">
              Terminé
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
